import React, { useCallback, useEffect } from "react";
import LibraryComicPdfPager from "./LibraryComicPdfPager";
import LibraryComicFlipbook from "./LibraryComicFlipbook";

/**
 * Fullscreen-style reader overlay. Same protected URLs as inline reader; unmounts inline viewer while open to avoid duplicate PDF fetches.
 */
export default function LibraryReaderExpandOverlay({
  open,
  onClose,
  title,
  pdfUrl,
  pageImages = [],
  startIndex = 0,
  onPageChange,
}) {
  const onKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [open, onClose]
  );

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onKey]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const usePdf = Boolean(pdfUrl) && !pageImages.length;

  return (
    <div className="lread-expand" role="dialog" aria-modal="true" aria-label="Expanded reader">
      <button type="button" className="lread-expand__backdrop" aria-label="Close reader" onClick={onClose} />
      <div className="lread-expand__panel">
        <header className="lread-expand__header">
          <h2 className="lread-expand__title serif">{title}</h2>
          <div className="lread-expand__actions">
            {usePdf && pdfUrl ? (
              <a className="lread-expand__link" href={pdfUrl} target="_blank" rel="noopener noreferrer">
                Open PDF
              </a>
            ) : null}
            <button type="button" className="lread-expand__close" onClick={onClose}>
              Close
            </button>
          </div>
        </header>
        <div className="lread-expand__body">
          {usePdf ? (
            <LibraryComicPdfPager
              variant="overlay"
              pdfUrl={pdfUrl}
              title={title}
              initialPageIndex={startIndex}
              onPageChange={onPageChange}
            />
          ) : pageImages.length ? (
            <LibraryComicFlipbook
              variant="overlay"
              pageImages={pageImages}
              pdfUrl={null}
              title={title}
              startImagePage={startIndex}
              pdfInitialPageIndex={startIndex}
              onPageChange={onPageChange}
            />
          ) : (
            <p className="lread-flipbook__error">Nothing to display in expanded view.</p>
          )}
        </div>
        <p className="lread-expand__hint">Press Escape to close · Arrow keys change pages when the reader has focus</p>
      </div>
    </div>
  );
}
